import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Pagination, Paper, Select,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { getActivityLogs } from '../../api';
import { SELECTED_MENU_PROPS } from '../StyledComponents/StyledComponent';

const PAGE_SIZE = 25;

const FIELD_LABELS = {
  employee_no: 'Employee ID', name: 'Name', email: 'Email', phone: 'Phone',
  mobile_number: 'Mobile number', branch: 'Branch', branches: 'Assigned branches',
  location: 'Location', password: 'Password', permissions: 'Permissions', status: 'Status',
  final_status: 'Final status', designation: 'Designation', department: 'Department',
  reporting_manager: 'Reporting manager', leave_type: 'Leave type', leave_days: 'Leave days',
  from_date: 'From date', to_date: 'To date', monthly_ctc: 'Monthly CTC', annual_ctc: 'Annual CTC',
  gross_pay: 'Gross pay', net_pay: 'Net pay', remarks: 'Remarks',
};

const labelFor = (key) => FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const displayValue = (key, value) => {
  if (key.toLowerCase().includes('password')) return 'Changed (value hidden)';
  if (value === '[REDACTED]') return 'Changed (sensitive value hidden)';
  if (value === null || value === undefined || value === '') return 'Empty';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

const formatDateTime = (value) => new Intl.DateTimeFormat('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
}).format(new Date(value));

const auditPresentation = (row) => {
  if (!row) return { breadcrumb: [], summary: '', fields: [] };
  const path = (row.path || '').toLowerCase();
  let section = ['Other activity'];
  if (path.includes('hr-contacts')) section = ['Global Settings', 'User Profile', 'HR Contacts'];
  else if (path.includes('leave')) section = ['Leaves', 'Leave Requests'];
  else if (path.includes('attendance')) section = ['Attendance'];
  else if (path.includes('candidate') || path.includes('interview')) section = ['Recruitment', path.includes('interview') ? 'Interviews' : 'Candidates'];
  else if (path.includes('form16') || path.includes('declaration')) section = ['Payroll', 'Form 16'];
  else if (path.includes('salary') || path.includes('payroll') || path.includes('payslip')) section = ['Payroll', 'Salary'];
  else if (path.includes('employee')) section = ['Employees', 'Employee Records'];
  else if (path.includes('admin/login') || path.includes('verify-otp')) section = ['Login'];

  const values = row.entity ? (row.new_values || {}) : (row.changes || {});
  const oldValues = row.old_values || {};
  const keys = row.entity ? Array.from(new Set([...Object.keys(oldValues), ...Object.keys(values)])) : Object.keys(values).filter((key) => key !== 'id');
  const fields = keys.map((key) => ({ key, label: labelFor(key), before: row.entity ? displayValue(key, oldValues[key]) : 'Not available', after: displayValue(key, values[key]) }));
  const actor = row.actor_name || row.actor_email || (row.actor_id ? `Employee ${row.actor_id}` : 'System');
  const target = values.name || oldValues.name || values.employee_name || values.employee_no || (row.entity ? `${row.entity}${row.object_id ? ` #${row.object_id}` : ''}` : section[section.length - 1]);
  const passwordChanged = keys.some((key) => key.toLowerCase().includes('password'));
  const verb = ({ CREATE: 'created', UPDATE: 'updated', DELETE: 'deleted', APPROVE: 'approved', REJECT: 'rejected', UPLOAD: 'uploaded to', GENERATE: 'generated', PUBLISH: 'published', REVOKE: 'revoked' })[row.action] || row.action?.toLowerCase() || 'performed an action on';
  let summary;
  if (row.action === 'LOGIN') summary = `${actor} ${row.success ? 'logged in successfully' : 'attempted to log in but failed'}.`;
  else if (passwordChanged) summary = `${actor} changed the password for ${target}.`;
  else summary = `${actor} ${verb} ${target}${keys.length ? ` (${keys.map(labelFor).join(', ')})` : ''}.`;
  const portal = row.actor_role === 'employee' ? 'Employee Portal' : 'Admin Dashboard';
  return { breadcrumb: ['Login', portal, ...section.filter((item) => item !== 'Login')], summary, fields };
};

export default function ActivityLogs() {
  const [filters, setFilters] = useState({ search: '', module: '', action: '', status: '', from: '', to: '' });
  const [query, setQuery] = useState(filters);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ results: [], count: 0, modules: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); setQuery(filters); }, 350);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getActivityLogs({ ...query, page, page_size: PAGE_SIZE })
      .then((result) => { if (active) { setData(result); setError(''); } })
      .catch((err) => { if (active) setError(err?.response?.data?.error || 'Unable to load activity logs.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query, page]);

  const change = (key) => (event) => setFilters((old) => ({ ...old, [key]: event.target.value }));
  const pages = Math.max(1, Math.ceil(data.count / PAGE_SIZE));
  const selectedPresentation = auditPresentation(selected);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={3}>
        <HistoryIcon color="success" fontSize="large" />
        <Box><Typography variant="h4" fontWeight={800}>Activity Logs</Typography><Typography color="text.secondary">Who changed what, when, and from where</Typography></Box>
      </Stack>
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
          <TextField size="small" label="Employee, email or path" value={filters.search} onChange={change('search')} sx={{ minWidth: 240 }} />
          <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Module</InputLabel><Select label="Module" MenuProps={SELECTED_MENU_PROPS} value={filters.module} onChange={change('module')}><MenuItem value="">All</MenuItem>{data.modules.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}</Select></FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}><InputLabel>Action</InputLabel><Select label="Action" MenuProps={SELECTED_MENU_PROPS} value={filters.action} onChange={change('action')}><MenuItem value="">All</MenuItem>{['LOGIN', 'CREATE', 'UPDATE', 'DELETE', 'APPROVE', 'REJECT', 'UPLOAD', 'GENERATE', 'PUBLISH', 'REVOKE'].map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}</Select></FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}><InputLabel>Status</InputLabel><Select label="Status" MenuProps={SELECTED_MENU_PROPS} value={filters.status} onChange={change('status')}><MenuItem value="">All</MenuItem><MenuItem value="success">Success</MenuItem><MenuItem value="failed">Failed</MenuItem></Select></FormControl>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.from} onChange={change('from')} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.to} onChange={change('to')} />
        </Stack>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
              <TableCell>Date & time</TableCell><TableCell>Employee</TableCell><TableCell>Role</TableCell>
              <TableCell>Action</TableCell><TableCell>What happened</TableCell><TableCell>Status</TableCell><TableCell>IP address</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={7} align="center" sx={{ py: 7 }}><CircularProgress /></TableCell></TableRow> : data.results.length === 0 ? <TableRow><TableCell colSpan={7} align="center" sx={{ py: 7 }}>No activity found.</TableCell></TableRow> : data.results.map((row) => {
              const presentation = auditPresentation(row);
              return (
                <TableRow hover key={row.id} onClick={() => setSelected(row)} sx={{ cursor: 'pointer' }}>
                  <TableCell>{formatDateTime(row.created_at)}</TableCell>
                  <TableCell><Typography fontWeight={600}>{row.actor_name || row.actor_email || 'System / unknown'}</Typography><Typography variant="caption" color="text.secondary">Employee ID: {row.actor_id || '—'}</Typography></TableCell>
                  <TableCell>{row.actor_role}</TableCell>
                  <TableCell><Chip size="small" label={row.action} color={row.action === 'DELETE' ? 'error' : row.action === 'CREATE' ? 'success' : 'primary'} /></TableCell>
                  <TableCell sx={{ minWidth: 300 }}><Typography variant="body2" fontWeight={600}>{presentation.summary}</Typography><Typography variant="caption" color="text.secondary">{presentation.breadcrumb.join(' → ')}</Typography></TableCell>
                  <TableCell><Chip size="small" variant="outlined" color={row.success ? 'success' : 'error'} label={row.success ? 'Success' : `Failed (${row.status_code})`} /></TableCell>
                  <TableCell>{row.ip_address || '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}><Typography color="text.secondary">{data.count} records</Typography><Pagination page={page} count={pages} onChange={(_, value) => setPage(value)} /></Stack>
      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            m: { xs: 1.5, sm: 3 },
            maxHeight: { xs: 'calc(100dvh - 24px)', sm: 'calc(100dvh - 48px)' },
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pr: 7,
            fontWeight: 800,
          }}
        >
          Activity details
          <IconButton
            aria-label="Close activity details"
            onClick={() => setSelected(null)}
            sx={{ position: 'absolute', right: 12, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflowY: 'auto', py: 2.5 }}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#eff6ff', borderColor: '#bfdbfe' }}>
            <Typography variant="caption" color="primary" fontWeight={800}>ACTIVITY PATH</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1, overflowWrap: 'anywhere' }}>
              {selectedPresentation.breadcrumb.join(' → ')}
            </Typography>
            <Typography fontWeight={800}>{selectedPresentation.summary}</Typography>
          </Paper>
          <Typography fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
            {selected?.entity ? `${selected.entity} #${selected.object_id}` : selected?.path}
          </Typography>
          {selected?.entity && (
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {selected?.path}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" mb={2} sx={{ overflowWrap: 'anywhere' }}>
            {selected?.user_agent || 'Device information unavailable'}
          </Typography>
          {selectedPresentation.fields.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: '38dvh' }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>Field touched</TableCell><TableCell>{selected?.entity ? 'Before' : 'Previous value'}</TableCell><TableCell>{selected?.entity ? 'After' : 'Submitted value'}</TableCell></TableRow></TableHead>
                <TableBody>{selectedPresentation.fields.map((field) => (
                  <TableRow key={field.key}><TableCell sx={{ fontWeight: 700 }}>{field.label}</TableCell><TableCell sx={{ overflowWrap: 'anywhere', color: 'text.secondary' }}>{field.before}</TableCell><TableCell sx={{ overflowWrap: 'anywhere' }}>{field.after}</TableCell></TableRow>
                ))}</TableBody>
              </Table>
            </TableContainer>
          )}
          <Box component="details" sx={{ '& summary': { cursor: 'pointer', fontWeight: 700, mb: 1 } }}>
            <summary>Technical details</summary>
          {selected?.entity ? (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
              <Box flex={1} minWidth={0}>
                <Typography fontWeight={700} mb={1}>Before</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff7ed', overflow: 'auto', maxHeight: '52dvh' }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13 }}>
                    {JSON.stringify(selected?.old_values || {}, null, 2)}
                  </pre>
                </Paper>
              </Box>
              <Box flex={1} minWidth={0}>
                <Typography fontWeight={700} mb={1}>After</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0fdf4', overflow: 'auto', maxHeight: '52dvh' }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13 }}>
                    {JSON.stringify(selected?.new_values || {}, null, 2)}
                  </pre>
                </Paper>
              </Box>
            </Stack>
          ) : (
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: '#0f172a', color: '#e2e8f0', overflow: 'auto', maxHeight: '58dvh' }}
            >
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13 }}>
                {JSON.stringify(selected?.changes || {}, null, 2)}
              </pre>
            </Paper>
          )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
